import { useEffect, useRef, useState } from 'react'

/**
 * Zahl, die beim Erscheinen einmal hochzählt.
 *
 * Die Bewegung hat eine Aufgabe: Sie sagt „das ist ein Stand von jetzt" und
 * lenkt den Blick auf die Zahl, die den Bildschirm trägt. Sie endet nach
 * knapp einer Sekunde in einem ruhenden Bild.
 *
 * Bei `prefers-reduced-motion` steht der Endwert sofort — und zwar über
 * denselben Weg, nicht über eine zweite Ausgabe: Ein Zweig, der nur bei
 * abgeschalteter Bewegung läuft, wird sonst nie geprüft.
 */

const DAUER_MS = 900

export interface ZahlProps {
  wert: number
  className?: string
}

export function Zahl({ wert, className }: ZahlProps) {
  const [gezeigt, setGezeigt] = useState(wert)
  const vorher = useRef(wert)

  useEffect(() => {
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const von = vorher.current
    vorher.current = wert

    if (ruhig || von === wert) {
      setGezeigt(wert)
      return
    }

    let laeuft = true
    const start = performance.now()
    const schritt = (jetzt: number) => {
      if (!laeuft) return
      const t = Math.min(1, (jetzt - start) / DAUER_MS)
      // Weiches Auslaufen — eine linear anhaltende Zahl wirkt abgeschnitten.
      const e = 1 - (1 - t) ** 3
      setGezeigt(Math.round(von + (wert - von) * e))
      if (t < 1) requestAnimationFrame(schritt)
    }
    requestAnimationFrame(schritt)

    return () => {
      laeuft = false
    }
  }, [wert])

  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {gezeigt.toLocaleString('de-DE')}
    </span>
  )
}
